import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Typography, Grid, Button, Chip,
  Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tooltip, useMediaQuery, useTheme
} from '@mui/material';
import {
  DateRange as DateRangeIcon,
  AttachMoney as MoneyIcon,
  AccessTime as HoursIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import StatusChip from '../StatusChip';

export default function PeriodDetailsDialog({
  open, onClose,
  periodDetails,
  formatDate, periodTypeLabels
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DateRangeIcon />
          {periodDetails?.name}
          <StatusChip status={periodDetails?.status} />
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {periodDetails && (
          <Box>
            {/* Summary Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <PeopleIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">
                      {periodDetails.stats?.employee_count || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Employees</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <DateRangeIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">
                      {periodDetails.stats?.entry_count || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Entries</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <HoursIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">
                      {parseFloat(periodDetails.stats?.total_hours || 0).toFixed(1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Hours</Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 1 }}>
                    <MoneyIcon color="primary" />
                    <Typography variant="h5" fontWeight="bold">
                      {periodDetails.stats?.total_pay == null ? (
                        <Tooltip title="Hidden — needs the View pay rates permission"><span>—</span></Tooltip>
                      ) : (
                        `$${parseFloat(periodDetails.stats.total_pay).toFixed(0)}`
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Total Pay</Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Period Info */}
            <Typography variant="subtitle2" gutterBottom>Period Details</Typography>
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Start Date</Typography>
                  <Typography>{formatDate(periodDetails.start_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">End Date</Typography>
                  <Typography>{formatDate(periodDetails.end_date)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Period Type</Typography>
                  <Typography>{periodTypeLabels[periodDetails.period_type]}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Paid/Unpaid</Typography>
                  <Typography>
                    {periodDetails.stats?.paid_count || 0} / {periodDetails.stats?.unpaid_count || 0}
                  </Typography>
                </Grid>
              </Grid>
              {periodDetails.notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">Notes</Typography>
                  <Typography>{periodDetails.notes}</Typography>
                </Box>
              )}
            </Box>

            {/* Employee Summary */}
            <Typography variant="subtitle2" gutterBottom>Employee Summary</Typography>
            {periodDetails.employees?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell align="right">Entries</TableCell>
                      <TableCell align="right">Hours</TableCell>
                      <TableCell align="right">Total Pay</TableCell>
                      <TableCell align="center">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {periodDetails.employees.map((emp) => (
                      <TableRow key={emp.employee_id}>
                        <TableCell>{emp.first_name} {emp.last_name}</TableCell>
                        <TableCell align="right">{emp.entry_count}</TableCell>
                        <TableCell align="right">{parseFloat(emp.total_hours).toFixed(1)}</TableCell>
                        <TableCell align="right">
                          {emp.total_pay == null ? (
                            <Tooltip title="Hidden — needs the View pay rates permission"><span>—</span></Tooltip>
                          ) : (
                            `$${parseFloat(emp.total_pay).toFixed(2)}`
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {emp.unpaid_count > 0 ? (
                            <Chip label={`${emp.unpaid_count} unpaid`} size="small" color="warning" />
                          ) : (
                            <Chip label="All paid" size="small" color="success" />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No entries in this pay period
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
